<script lang="ts" setup>
import {
  BeamShapingGroupDto,
  instanceOfBeamShapingGroupRectangularDto,
  ISingleSystemSetupParameter,
  MedicalSystemSetupBaseDto,
  TaskTypeDescription,
} from '@/api/generator';
import { SelectItemType } from '@/components/forms/types.ts';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { DataTableHeader, OverviewTableProps } from '@/components/OverviewTable/types.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import BeamShapingGroup from '@/views/tasks/components/beamShaping/BeamShapingGroup.vue';
import {
  bccBeamShapingRectangularFieldSizeXOptionsInjectionKey,
  getBeamShapingTemplate,
} from '@/views/tasks/components/beamShaping/utils.ts';
import { useFieldArray, useFormErrors } from 'vee-validate';
import { computed, nextTick, provide, toRef, watch } from 'vue';
import FormAddButton from '../FormAddButton.vue';

import { useTranslation } from 'i18next-vue';
import { useFormContext } from '@/components/forms/useFormContext';
import { pick } from 'lodash';
const { t } = useTranslation();

export type BeamShapingConfigurationProps = {
  name: string; // taskItemArea[{areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings`"
  taskType: TaskTypeDescription;
  containerEnabled: boolean;
  medicalSystemSetup: MedicalSystemSetupBaseDto;
};

const props = defineProps<BeamShapingConfigurationProps>();

const mode = useFormMode();

const formErrors = useFormErrors();

const {
  fields: items,
  remove,
  push: addBeamShapingEntry,
  replace,
} = useFieldArray<BeamShapingGroupDto>(toRef(() => props.name));

const { formContext } = useFormContext();

const childPaths = computed(() => items.value.map((_field, index) => `${props.name}[${index}]`));

async function validate() {
  // Removed or switched rows (un)register their fields on the next render flush; validate afterwards
  // so vee-validate sees the current paths.
  await nextTick();
  await Promise.all(childPaths.value.map((path) => formContext.validateField(path)));
}

const rowErrors = computed(() => pick(formErrors.value, childPaths.value));

const rowValidationMessage = computed(() => Object.values(rowErrors.value)[0]);

async function removeBeamShapingEntry(index: number) {
  remove(index);
  await validate();
}

const beamConstancyFieldSizeXOptions = computed<SelectItemType<number>[]>(() => [
  {
    title: '10.00',
    value: 10.0,
    props: {
      disabled: items.value
        .map((item) =>
          instanceOfBeamShapingGroupRectangularDto(item) ? item.radiationFieldSizeX?.value : NaN,
        )
        .includes(10.0),
    },
  },
  {
    title: '20.00',
    value: 20.0,
    props: {
      disabled: items.value
        .map((item) =>
          instanceOfBeamShapingGroupRectangularDto(item) ? item.radiationFieldSizeX?.value : NaN,
        )
        .includes(20.0),
    },
  },
]);

provide(bccBeamShapingRectangularFieldSizeXOptionsInjectionKey, beamConstancyFieldSizeXOptions);

const medicalSystemBeamShapingHeaders = computed<
  OverviewTableProps<ISingleSystemSetupParameter, string>['headers']
>(() => {
  const headers: DataTableHeader<string>[] = [
    {
      title: t('RadiationFieldShape'),
      key: 'radiationFieldShape',
      sortable: false,
    },
    {
      title: t('RadiationFieldSymmetry'),
      key: 'radiationFieldSymmetry',
      sortable: false,
    },
    {
      title: t('RadiationFieldSize'),
      key: 'radiationFieldSize',
      sortable: false,
    },
    {
      title: t('RadiationFieldEdge'),
      key: 'radiationFieldEdge',
      sortable: false,
    },
    {
      title: '',
      key: 'remove',
      sortable: false,
    },
  ];

  return headers;
});

async function addParameter() {
  const newBeamShaping = await getBeamShapingTemplate(props.medicalSystemSetup, props.taskType);

  if (newBeamShaping) {
    addBeamShapingEntry(newBeamShaping);
  }
}

// when BeamShapingConfiguration container
//   was enabled --> add a new parameter
//   was disabled --> remove all parameters
watch(
  () => props.containerEnabled,
  (containerEnabled) => {
    if (!containerEnabled) {
      replace([]);
    } else if (items.value.length === 0) {
      addParameter();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="beam-shaping-table">
    <v-alert
      v-if="rowValidationMessage"
      type="error"
      variant="tonal"
      class="text-body-1 text-center"
    >
      <template #text>
        {{ rowValidationMessage }}
      </template>
    </v-alert>

    <div class="d-flex">
      <VSpacer />

      <Tooltip
        v-if="taskType === 'BeamConstancyCheck'"
        position="top"
        :disabled="taskType === 'BeamConstancyCheck' && items.length < 2"
      >
        <template #target="{ props: tooltipProps }">
          <div v-bind="tooltipProps" :style="{ position: 'relative' }">
            <FormAddButton
              v-if="mode !== 'details'"
              :label="'AddBeamShapingParameter'"
              :disabled="items.length === 2"
              @click="addParameter"
            />
          </div>
        </template>

        <template #tooltip>
          <p :style="{ maxWidth: '500px', lineHeight: '20px' }">
            {{ $t('AddBeamShapingParametersTooltip') }}
          </p>
        </template>
      </Tooltip>

      <FormAddButton
        v-else-if="mode !== 'details'"
        :label="'AddBeamShapingParameter'"
        @click="addParameter"
      />
    </div>

    <OverviewTable
      v-if="items"
      :headers="medicalSystemBeamShapingHeaders"
      :items="items"
      data-testid="DataTable_BeamShaping"
      :zebra="true"
      :noSpacing="true"
      :showItemsCount="false"
    >
      <template #item="{ index, item: { key } }">
        <BeamShapingGroup
          :key="key"
          :name="`${name}[${index}]`"
          :taskType="taskType"
          :canBeDeleted="items.length > 1"
          :class="{ 'has-error': rowErrors[`${name}[${index}]`] }"
          @remove="removeBeamShapingEntry(index)"
          @blur="validate()"
          @switchShape="validate()"
        />
      </template>
    </OverviewTable>
  </div>
</template>

<style lang="scss" scoped>
:deep(.overviewTable) {
  .v-data-table table tr {
    &.has-error {
      background-color: rgb(var(--v-theme-error), 20%) !important;
    }
  }
}
</style>
